import { useState } from 'react';
import { CalendarDays, MapPin, CheckCircle2 } from 'lucide-react';
import useFetch from '../../hooks/useFetch';
import { useEvents } from '../../context/EventContext';
import { Card, Badge, Loader, EmptyState, SectionTitle } from '../../components/UI';
import { fmtDate, fmtTime } from '../../utils/format';
import { SessionPoll, chipStyle } from './shared';

export default function AttendeeSchedule() {
  const { event, eventId } = useEvents();
  const { data: days, loading, reload } = useFetch(eventId ? `/events/${eventId}/schedule` : null, {}, { skip: !eventId });
  const [dayIndex, setDayIndex] = useState(0);

  if (loading) return <Loader full label="Loading the agenda" />;
  if (!days?.length) {
    return <EmptyState message="No sessions published yet" hint="The agenda appears here as soon as the organiser publishes it." icon={CalendarDays} />;
  }

  const day = days[Math.min(dayIndex, days.length - 1)];
  const now = Date.now();

  return (
    <>
      <SectionTitle title="Schedule" subtitle={event?.title} />

      {days.length > 1 && (
        <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginBottom: 18 }}>
          {days.map((d, i) => (
            <button key={d.date} type="button" style={chipStyle(i === dayIndex)} onClick={() => setDayIndex(i)}>
              Day {i + 1} · {fmtDate(d.date)}
            </button>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 820 }}>
        {day.sessions.map((s) => {
          const live = now >= new Date(s.startAt).getTime() && now <= new Date(s.endAt || s.startAt).getTime();
          const extras = (s.customFields || []).filter((f) => f.value);
          return (
            <Card key={s._id} padding={18} style={live ? { borderColor: 'rgba(239,212,138,0.4)' } : undefined}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--gold-400)' }}>
                  {fmtTime(s.startAt)}{s.endAt ? ` – ${fmtTime(s.endAt)}` : ''}
                </span>
                {live && <Badge tone="gold" dot>Live now</Badge>}
                {s.attended && <Badge tone="success"><CheckCircle2 size={10} /> Attended</Badge>}
                {s.isMandatory && <Badge tone="danger">Mandatory</Badge>}
                {(s.customType || s.type) && <Badge tone="neutral">{s.customType || s.type}</Badge>}
              </div>

              <h3 style={{ fontSize: 16, fontWeight: 600, marginTop: 8 }}>{s.title}</h3>
              {s.description && <p style={{ fontSize: 13, color: 'var(--text-mid)', marginTop: 5, lineHeight: 1.6 }}>{s.description}</p>}

              {(s.hall || s.venue || extras.length > 0) && (
                <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 9, fontSize: 12.5, color: 'var(--text-lo)' }}>
                  {(s.hall || s.venue) && <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><MapPin size={12} />{s.hall || s.venue}</span>}
                  {extras.filter((f) => f.type !== 'image').map((f) => (
                    <span key={f.key}><span style={{ opacity: 0.7 }}>{f.label}:</span> {f.value}</span>
                  ))}
                </div>
              )}

              {extras.filter((f) => f.type === 'image').map((f) => (
                <img key={f.key} src={f.value} alt={f.label}
                  style={{ width: '100%', maxHeight: 240, objectFit: 'cover', borderRadius: 'var(--r)', marginTop: 12 }} />
              ))}

              {s.attendancePoll?.enabled && <SessionPoll session={s} eventId={eventId} onAnswered={reload} />}
            </Card>
          );
        })}
      </div>
    </>
  );
}
